import React, { useState } from "react";
import {
  TrendingDown,
  ArrowLeftRight,
  Plus,
  Save,
  Trash2,
  ToggleLeft,
  ToggleRight,
  CheckCircle2,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui";
import { AlertMessage } from "../../components/ui";
import { ModernInput, ModernSelect } from "../../components/ui";
import { useDepreciationRules, DepreciationRule } from "../../hooks/useDepreciationRules";
import { useTradeAcceptanceConfig } from "../../hooks/useTradeAcceptanceConfig";

export const VehicleEvaluationPage: React.FC = () => {
  // ── Depreciation rules ────────────────────────────────────────────────────
  const {
    rules: depreciationRules,
    isLoading: isLoadingDepRules,
    error: depRulesError,
    addRule: addDepreciationRule,
    updateRule: updateDepreciationRule,
    deleteRule: deleteDepreciationRule,
  } = useDepreciationRules();

  const [newDepRule, setNewDepRule] = useState<
    Omit<DepreciationRule, "id" | "createdAt" | "updatedAt">
  >({
    label: "",
    type: "mileage",
    threshold: 0,
    conditionValue: "",
    percentage: -5,
    isActive: true,
  });
  const [showDepRuleForm, setShowDepRuleForm] = useState(false);
  const [depRuleOpError, setDepRuleOpError] = useState<string | null>(null);

  // ── Trade acceptance config ────────────────────────────────────────────────
  const {
    config: tradeConfig,
    saveConfig: saveTradeConfig,
    isSaved: tradeSaved,
  } = useTradeAcceptanceConfig();

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const tabs = [
    {
      id: "depreciation",
      label: "Desvalorização",
      icon: <TrendingDown className="h-4 w-4" />,
      badge: depreciationRules.filter((r) => r.isActive).length,
    },
    {
      id: "trade-acceptance",
      label: "Aceitação na Troca",
      icon: <ArrowLeftRight className="h-4 w-4" />,
    },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Avaliação de Veículos
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure regras de desvalorização e critérios de aceitação de
          veículos na troca
        </p>
      </div>

      <Tabs defaultValue="depreciation" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="flex items-center gap-2"
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-muted text-muted-foreground">
                  {tab.badge}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ── Tab: Desvalorização ─────────────────────────────────────────── */}
        <TabsContent value="depreciation">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  Regras de Desvalorização
                </h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Configure fatores que afetam o valor de veículos na troca.
                  Para km e idade, aplica-se apenas a maior faixa correspondente.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowDepRuleForm((v) => !v);
                  setDepRuleOpError(null);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-accent text-white rounded-md hover:bg-accent/90 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Nova Regra
              </button>
            </div>

            {depRulesError && (
              <AlertMessage variant="error" message={depRulesError} />
            )}
            {depRuleOpError && (
              <AlertMessage variant="error" message={depRuleOpError} />
            )}

            {/* New rule form */}
            {showDepRuleForm && (
              <Card>
                <CardContent className="p-5 space-y-4">
                  <h3 className="font-medium text-foreground">Nova Regra</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Descrição *
                      </label>
                      <ModernInput
                        placeholder="Ex: Km acima de 80.000"
                        value={newDepRule.label}
                        onChange={(e) =>
                          setNewDepRule((p) => ({ ...p, label: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Tipo *
                      </label>
                      <select
                        value={newDepRule.type}
                        onChange={(e) =>
                          setNewDepRule((p) => ({
                            ...p,
                            type: e.target.value as DepreciationRule["type"],
                          }))
                        }
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="mileage">Quilometragem</option>
                        <option value="age">Idade do Veículo (anos)</option>
                        <option value="condition">Condição</option>
                      </select>
                    </div>

                    {(newDepRule.type === "mileage" ||
                      newDepRule.type === "age") && (
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          {newDepRule.type === "mileage"
                            ? "A partir de (km)"
                            : "A partir de (anos)"}
                        </label>
                        <ModernInput
                          type="number"
                          placeholder="0"
                          value={newDepRule.threshold?.toString() || ""}
                          onChange={(e) =>
                            setNewDepRule((p) => ({
                              ...p,
                              threshold: parseInt(e.target.value) || 0,
                            }))
                          }
                        />
                      </div>
                    )}

                    {newDepRule.type === "condition" && (
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Valor da Condição
                        </label>
                        <ModernInput
                          placeholder="Ex: damaged"
                          value={newDepRule.conditionValue || ""}
                          onChange={(e) =>
                            setNewDepRule((p) => ({
                              ...p,
                              conditionValue: e.target.value,
                            }))
                          }
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Percentual (negativo = desvalorização)
                      </label>
                      <ModernInput
                        type="number"
                        placeholder="-10"
                        value={newDepRule.percentage.toString()}
                        onChange={(e) =>
                          setNewDepRule((p) => ({
                            ...p,
                            percentage: parseInt(e.target.value) || 0,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!newDepRule.label.trim()) {
                          setDepRuleOpError("A descrição é obrigatória");
                          return;
                        }
                        try {
                          setDepRuleOpError(null);
                          await addDepreciationRule(newDepRule);
                          setNewDepRule({
                            label: "",
                            type: "mileage",
                            threshold: 0,
                            conditionValue: "",
                            percentage: -5,
                            isActive: true,
                          });
                          setShowDepRuleForm(false);
                        } catch (err: any) {
                          setDepRuleOpError(
                            err?.message || "Erro ao salvar regra"
                          );
                        }
                      }}
                      className="flex items-center gap-1.5 px-4 py-2 bg-accent text-white text-sm rounded-md hover:bg-accent/90 transition-colors"
                    >
                      <Save className="h-4 w-4" />
                      Salvar Regra
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDepRuleForm(false);
                        setDepRuleOpError(null);
                      }}
                      className="px-4 py-2 text-sm border border-border rounded-md hover:bg-surface transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Rules table */}
            <Card>
              <CardContent className="p-0">
                {isLoadingDepRules ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto" />
                  </div>
                ) : depreciationRules.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <TrendingDown className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm">
                      Nenhuma regra configurada. Clique em "Nova Regra" para
                      começar.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    <div className="grid grid-cols-[1fr_120px_100px_80px_80px] gap-4 px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide bg-surface">
                      <span>Descrição</span>
                      <span>Tipo</span>
                      <span>Valor Limite</span>
                      <span className="text-right">%</span>
                      <span className="text-right">Ações</span>
                    </div>
                    {depreciationRules.map((rule) => (
                      <div
                        key={rule.id}
                        className={`grid grid-cols-[1fr_120px_100px_80px_80px] gap-4 px-5 py-3 items-center ${
                          !rule.isActive ? "opacity-50" : ""
                        }`}
                      >
                        <span className="text-sm text-foreground">
                          {rule.label}
                        </span>
                        <span className="text-sm text-muted-foreground capitalize">
                          {rule.type === "mileage"
                            ? "Quilometragem"
                            : rule.type === "age"
                            ? "Idade"
                            : "Condição"}
                        </span>
                        <span className="text-sm text-muted-foreground font-mono">
                          {rule.type === "mileage"
                            ? `${rule.threshold?.toLocaleString("pt-BR")} km`
                            : rule.type === "age"
                            ? `${rule.threshold} anos`
                            : rule.conditionValue || "-"}
                        </span>
                        <span
                          className={`text-sm font-semibold text-right tabular-nums ${
                            rule.percentage < 0
                              ? "text-destructive"
                              : "text-accent"
                          }`}
                        >
                          {rule.percentage > 0 ? "+" : ""}
                          {rule.percentage}%
                        </span>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await updateDepreciationRule(rule.id, {
                                  isActive: !rule.isActive,
                                });
                              } catch (err: any) {
                                setDepRuleOpError(
                                  err?.message || "Erro ao atualizar regra"
                                );
                              }
                            }}
                            className="p-1.5 rounded text-muted-foreground hover:text-foreground transition-colors"
                            title={rule.isActive ? "Desativar" : "Ativar"}
                          >
                            {rule.isActive ? (
                              <ToggleRight className="h-4 w-4 text-accent" />
                            ) : (
                              <ToggleLeft className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!confirm("Excluir esta regra?")) return;
                              try {
                                await deleteDepreciationRule(rule.id);
                              } catch (err: any) {
                                setDepRuleOpError(
                                  err?.message || "Erro ao excluir regra"
                                );
                              }
                            }}
                            className="p-1.5 rounded text-muted-foreground hover:text-destructive transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── Tab: Aceitação na Troca ─────────────────────────────────────── */}
        <TabsContent value="trade-acceptance">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowLeftRight className="h-5 w-5 text-accent" />
                  Critérios de Aceitação de Veículos na Troca
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">

                {/* Quilometragem */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-border">
                    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Quilometragem
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        Quilometragem máxima aceita
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Deixe em branco para aceitar qualquer quilometragem
                      </p>
                    </div>
                    <div className="w-40">
                      <ModernInput
                        type="number"
                        value={tradeConfig.maxMileage ?? ""}
                        onChange={(e) =>
                          saveTradeConfig({
                            maxMileage: e.target.value
                              ? parseInt(e.target.value)
                              : null,
                          })
                        }
                        placeholder="Sem limite"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Idade */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-border">
                    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Idade do Veículo
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        Idade máxima em anos
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Deixe em branco para aceitar qualquer ano
                      </p>
                    </div>
                    <div className="w-40">
                      <ModernInput
                        type="number"
                        value={tradeConfig.maxAge ?? ""}
                        onChange={(e) =>
                          saveTradeConfig({
                            maxAge: e.target.value
                              ? parseInt(e.target.value)
                              : null,
                          })
                        }
                        placeholder="Sem limite"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Condição */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-border">
                    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Condição
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        Condição mínima aceita
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Veículos com condição inferior serão sinalizados
                      </p>
                    </div>
                    <div className="w-52">
                      <ModernSelect
                        value={tradeConfig.minCondition}
                        onChange={(e) =>
                          saveTradeConfig({ minCondition: e.target.value })
                        }
                        options={[
                          { value: "", label: "Qualquer condição" },
                          { value: "excellent", label: "Excelente ou acima" },
                          { value: "good", label: "Bom ou acima" },
                          { value: "regular", label: "Regular ou acima" },
                        ]}
                      />
                    </div>
                  </div>
                </div>

                {/* Alienação */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-border">
                    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Alienação / Financiamento
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        Aceitar veículos alienados
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Permite incluir veículos com financiamento ou ônus na troca
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={tradeConfig.acceptAlienated}
                      onClick={() =>
                        saveTradeConfig({
                          acceptAlienated: !tradeConfig.acceptAlienated,
                        })
                      }
                      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                        tradeConfig.acceptAlienated ? "bg-accent" : "bg-border"
                      }`}
                    >
                      <span
                        className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                          tradeConfig.acceptAlienated
                            ? "translate-x-4"
                            : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>

                  {tradeConfig.acceptAlienated && (
                    <div className="flex items-center justify-between gap-4 pl-4 border-l-2 border-accent/30">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">
                          Dívida máxima (% do valor ofertado)
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Ex: 50 = dívida não pode ultrapassar 50% do valor
                        </p>
                      </div>
                      <div className="w-28">
                        <ModernInput
                          type="number"
                          value={tradeConfig.maxAlienatedDebtRatio}
                          onChange={(e) =>
                            saveTradeConfig({
                              maxAlienatedDebtRatio:
                                parseFloat(e.target.value) || 0,
                            })
                          }
                          min="0"
                          max="100"
                          placeholder="100"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Avaliação automática */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-border">
                    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                      Avaliação Automática
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        Aplicar desvalorização automaticamente
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Pré-aplica as regras de desvalorização ao abrir a avaliação
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={tradeConfig.autoApplyDepreciation}
                      onClick={() =>
                        saveTradeConfig({
                          autoApplyDepreciation:
                            !tradeConfig.autoApplyDepreciation,
                        })
                      }
                      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                        tradeConfig.autoApplyDepreciation
                          ? "bg-accent"
                          : "bg-border"
                      }`}
                    >
                      <span
                        className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                          tradeConfig.autoApplyDepreciation
                            ? "translate-x-4"
                            : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        Exigir valor FIPE para salvar
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Bloqueia o salvamento da avaliação se a FIPE não for encontrada
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={tradeConfig.requireFipe}
                      onClick={() =>
                        saveTradeConfig({ requireFipe: !tradeConfig.requireFipe })
                      }
                      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                        tradeConfig.requireFipe ? "bg-accent" : "bg-border"
                      }`}
                    >
                      <span
                        className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                          tradeConfig.requireFipe
                            ? "translate-x-4"
                            : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {tradeSaved && (
                  <div className="flex items-center gap-2 text-accent text-sm font-medium">
                    <CheckCircle2 className="h-4 w-4" />
                    Configurações salvas automaticamente
                  </div>
                )}

              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
